import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Database, Loader2, ShieldCheck, ShieldAlert, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { rootApi } from "@/lib/api-client";

export default function CommandDatabase() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState(null);
  const [view, setView] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.is_root_admin) return;
    rootApi.status().then(setStatus).catch(() => setStatus({ full_control: false }));
    rootApi.collections().then((r) => setCollections(r.collections)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    rootApi.collection(active, page, search)
      .then(setView).catch(() => setView(null)).finally(() => setLoading(false));
  }, [active, page, search]);

  if (!user?.is_root_admin) {
    return (
      <div className="min-h-screen uc-grain flex items-center justify-center">
        <div className="text-rose-400 font-mono-uc text-sm uppercase" data-testid="root-denied">Root Admin only</div>
      </div>
    );
  }

  const totalPages = view ? Math.max(1, Math.ceil(view.total / view.page_size)) : 1;

  return (
    <div className="min-h-screen uc-grain relative" data-testid="command-database-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="uc-chip flex items-center gap-1.5 hover:bg-white/5" data-testid="cdb-back-btn">
              <ArrowLeft size={11} /> Dashboard
            </Link>
            <div>
              <div className="font-mono-uc text-[9px] uppercase tracking-[0.3em] text-rose-300/80">ROOT ADMIN</div>
              <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
                <Database size={20} className="text-amber-400" /> Command Database
              </h1>
            </div>
          </div>
          {status && (
            <span className={`uc-chip font-mono-uc text-[10px] uppercase flex items-center gap-1.5 ${
              status.full_control ? "text-emerald-300 border-emerald-500/60 bg-emerald-500/10" : "text-rose-300 border-rose-500/50"}`}
              data-testid="sovereign-control-badge">
              {status.full_control ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
              {status.full_control
                ? `Database Access: Full Sovereign Control · ${status.collection_count} collections`
                : "ACCESS DEGRADED — check backend"}
            </span>
          )}
        </div>

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-3 border border-white/10 bg-black/40 p-3 max-h-[75vh] overflow-y-auto uc-scroll" data-testid="cdb-collection-list">
            {collections.map((c) => (
              <button key={c.name}
                onClick={() => { setActive(c.name); setPage(1); setSearch(""); }}
                className={`w-full text-left px-3 py-2 text-xs font-mono-uc flex justify-between items-center hover:bg-white/5 ${
                  active === c.name ? "bg-amber-500/10 text-amber-400" : "text-white/70"}`}
                data-testid={`cdb-coll-${c.name}`}>
                <span className="truncate">{c.name}</span>
                <span className="text-white/30 ml-2">{c.count}</span>
              </button>
            ))}
          </div>

          <div className="col-span-12 md:col-span-9 border border-white/10 bg-black/40 p-4 max-h-[75vh] overflow-y-auto uc-scroll">
            {!active ? (
              <div className="text-white/40 text-sm py-20 text-center" data-testid="cdb-placeholder">
                Select a collection — every record in the estate is readable and writable from this panel.
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="font-mono-uc text-[11px] uppercase text-amber-400">
                    {active} <span className="text-white/40">· {view?.total ?? "…"} records</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/30" />
                      <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        placeholder="search…"
                        className="bg-black/60 border border-white/15 pl-7 pr-2 py-1.5 text-xs text-white w-44 focus:border-amber-500/50 outline-none"
                        data-testid="cdb-search-input" />
                    </div>
                    <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                      className="uc-chip disabled:opacity-30" data-testid="cdb-prev-page"><ChevronLeft size={12} /></button>
                    <span className="font-mono-uc text-[10px] text-white/50">{page}/{totalPages}</span>
                    <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                      className="uc-chip disabled:opacity-30" data-testid="cdb-next-page"><ChevronRight size={12} /></button>
                  </div>
                </div>
                {loading ? (
                  <div className="flex justify-center py-10"><Loader2 className="animate-spin text-amber-400" /></div>
                ) : (
                  <div className="space-y-2" data-testid="cdb-documents">
                    {(view?.documents || []).map((d, i) => (
                      <pre key={d._id || d.id || i}
                        className="text-[10px] text-white/70 bg-black/60 border border-white/5 p-2 overflow-x-auto uc-scroll whitespace-pre-wrap break-all">
                        {JSON.stringify(d, null, 1)}
                      </pre>
                    ))}
                    {view && view.documents.length === 0 && (
                      <div className="text-white/40 text-xs py-8 text-center">No records match.</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
